import { useEffect, useRef, useCallback } from "react";
import { useParams } from "react-router-dom";
import { useChapterStore } from "@/zapi-frontend/stores/chapter";
import { useMeStore } from "@/zapi-frontend/shared/hooks/useMe";
import { useCommentStore } from "../model/store/commentsStore";
import { useEditorStore } from "@/zapi-frontend/stores/editor";
import { CommentsHeader } from "./_CommentsHeader";
import { getMarkedText, removeMark } from "../lib/lib";
import { LoadingSpinner, Typography } from "@libs/zapi-ui/custom";
import { useProjectSidebarStore } from "@/zapi-frontend/stores/projectSidebarStore";
import { Comment } from "./Comment";
import { useProjectStore } from "@/zapi-frontend/stores/project";
import { CommentInputField } from "./_CommentInputField";

export function CommentsIsland() {
  const { id } = useParams();

  const scrollableCommentsRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Tech debt: move to chapter entity
  const { activeChapter } = useChapterStore();
  // Tech debt: move to user entity
  const { firstName, lastName, id: userId } = useMeStore();
  const { isFetchProjectLoading } = useProjectStore();
  const { getComments, isLoadingComments, getCommentsError, fetchComments, deleteComment } = useCommentStore();
  const { setScrollToBottomCallback } = useProjectSidebarStore();
  const { commentsUpdated, removeCommentMark } = useEditorStore();

  const projectId = id || "";
  const chapterId = activeChapter?.id?.toString() || "";
  const comments = getComments(chapterId);
  const isCommentsLoading = isLoadingComments(chapterId);
  const commentsError = getCommentsError(chapterId);

  useEffect(() => {
    if (projectId && chapterId) fetchComments(projectId, chapterId);
  }, [projectId, chapterId, fetchComments]);

  const scrollToBottom = () => {
    if (scrollableCommentsRef.current) {
      scrollableCommentsRef.current.scrollTo({ top: scrollableCommentsRef.current.scrollHeight, behavior: "smooth" });
    }
  };

  useEffect(() => {
    setScrollToBottomCallback(scrollToBottom);

    return () => {
      setScrollToBottomCallback(null);
    };
  }, [setScrollToBottomCallback]);

  const handleDeleteComment = useCallback(
    async (commentId: string) => {
      if (!projectId || !chapterId) return;
      await deleteComment(projectId, chapterId, commentId);
      removeMark(commentId);
      removeCommentMark?.(commentId);
      commentsUpdated?.();
    },
    [projectId, chapterId, removeCommentMark, commentsUpdated, deleteComment]
  );

  const isLoading = isCommentsLoading || isFetchProjectLoading;
  const isEmpty = !commentsError && !isLoading && comments.length === 0;

  return (
    <div className="w-full flex flex-col h-full" data-testid="comments-root">
      <CommentsHeader />
      <div
        ref={scrollableCommentsRef}
        className="h-full overflow-y-auto p-1 bg-innoveraZinc-100"
        data-testid="comments-scrollable"
      >
        {isLoading && (
          <div className="h-full flex flex-col p-2 gap-2 items-center justify-center">
            <LoadingSpinner />
            <Typography variant="body3" className="text-innoveraZinc-500 text-center font-light">
              Loading comments...
            </Typography>
          </div>
        )}

        {(isEmpty || commentsError) && !isLoading && (
          <div className="h-full flex justify-center items-center">
            <Typography variant="body3" className="text-innoveraZinc-500 text-center font-light">
              No comments yet
            </Typography>
          </div>
        )}

        {!isLoading && userId && (
          <div className="flex flex-col gap-1">
            {comments.map((comment) => {
              const markedText = getMarkedText(comment.id);
              return (
                <Comment key={comment.id} comment={comment} onDelete={handleDeleteComment} markedText={markedText} />
              );
            })}
          </div>
        )}
      </div>

      {userId && (
        <CommentInputField
          ref={textareaRef}
          onSubmit={scrollToBottom}
          isLoading={isCommentsLoading}
          userId={userId}
          firstName={firstName}
          lastName={lastName}
        />
      )}
    </div>
  );
}
